/**
 * variant-picker-models.js
 *
 * NOT a custom element, and does not redefine <variant-picker> - that tag is
 * already registered by the shared theme.js VariantPicker class, and
 * ProductInfo elsewhere in theme.js hardcodes a querySelector('variant-picker')
 * lookup that depends on the real tag/class being used untouched. Redefining
 * it (or using a different tag name) breaks that lookup or throws outright.
 *
 * Instead, this is a supplementary enhancement: it finds whichever
 * <variant-picker> element(s) carry the [data-all-variants] marker (only
 * product-variant-picker-dropdown-models.liquid embeds that, so this only
 * ever touches "the-snowball-models", never any other product) and adds a
 * SECOND, independent 'change' listener alongside the original class's own.
 * Listener registration order matters here: the native VariantPicker's
 * connectedCallback runs first (during custom element upgrade, before this
 * deferred script executes), so its listener - which dispatches to
 * ProductInfo and reads the clicked <option> synchronously - always fires
 * and completes before this script's listener runs. This script's refresh()
 * then rebuilds the <option> lists afterward.
 *
 * Re-setup on every swap: after a selection, ProductInfo
 * (ProductInfo.handleUpdateProductInfo in theme.js) replaces the picker's
 * entire DOM subtree with a fresh server-rendered copy
 * (theme.HTMLUpdateUtility.viewTransition) UNCONDITIONALLY - but it only
 * publishes theme.pubsub.PUB_SUB_EVENTS.variantChange when that swap
 * resolves to a COMPLETE variant; for an incomplete combination it returns
 * early (this.setUnavailable(); return;) without ever publishing. Relying on
 * that pubsub event to know when to re-run is therefore unreliable - it
 * silently never fires for an incomplete pick, e.g. choosing Model alone
 * before Size/Base. That's also exactly the case where the fresh copy would
 * otherwise be wrong: none of Model, Size or Base ever gets a server-side
 * default (product-variant-picker-dropdown-models.liquid always re-renders
 * every <select> back to its blank placeholder), so without extra help every
 * pick the shopper has already made would visibly "reset" on each
 * subsequent change. A MutationObserver is used instead of the pubsub event,
 * since it fires on the DOM swap itself and doesn't depend on which pubsub
 * events ProductInfo decides to publish. This script tracks the shopper's
 * last known picks itself (independent of whether a full variant ever
 * resolves) in the module-level `lastSelections`, and re-applies them - to
 * both the <select> elements and their "Model: ___" / "Size: ___" / "Base:
 * ___" label text - to every freshly swapped-in subtree before re-filtering.
 *
 * Behavior:
 * - Model is never filtered; Size is filtered by Model only; Base is
 *   filtered by Model and Size (a strict hierarchy, not a symmetric
 *   constraint system).
 * - Option values that can't form a real variant given the preceding
 *   selection(s) are removed from their <select> entirely, not grayed out.
 * - If, after filtering, a select has exactly one possible value left and
 *   the shopper hasn't chosen one yet, it's auto-selected - except Size,
 *   which must always be a deliberate shopper choice.
 * - The submit button is only ever enabled once ALL THREE selects have an
 *   explicit value (including a legitimately auto-filled one, e.g. a model
 *   with only one Base). This does NOT just mirror native behavior with
 *   nicer text - Shopify's own option_values resolution is not strict: given
 *   only Model+Size (Base left unspecified), ProductInfo still resolves a
 *   real, complete variant by silently filling in Base with whichever value
 *   happens to match first, instead of treating the pick as incomplete. That
 *   enables the real "Add to cart" button and writes that arbitrary variant's
 *   id into the form's hidden id input - letting a shopper add an Undead/
 *   Bounty Hunter board to cart with a Base they never actually chose. This
 *   script re-checks completeness itself after every swap (via the same
 *   selects it's already tracking) and forcibly disables the button / blanks
 *   the hidden id input whenever Shopify's resolution disagrees, scoped to
 *   this product only via the closest <product-info>.
 */
(function () {
  function updateSelectedAttribute(select) {
    if (select.selectedOptions.length) {
      Array.from(select.options).find((o) => o.hasAttribute('selected'))?.removeAttribute('selected');
      select.selectedOptions[0].setAttribute('selected', '');
    }
  }

  // Survives each DOM subtree swap; see the file-level comment above.
  let lastSelections = null;

  function setUpPicker(picker) {
    const dataEl = picker.querySelector('script[data-all-variants]');
    if (!dataEl) return null;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      console.error('variant-picker-models: failed to parse variant data', e);
      return null;
    }
    if (!variants.length) return null;

    const selects = Array.from(picker.querySelectorAll('select[data-option-name]'));
    if (!selects.length) return null;

    const originalOptions = new Map();
    const labelValueEls = new Map();
    selects.forEach((select) => {
      originalOptions.set(select, Array.from(select.options).map((o) => o.cloneNode(true)));

      // The "Model:"/"Size:"/"Base:" label row's value span - server-rendered
      // with the "Select {name}" placeholder (see refresh/syncLabels below).
      const wrapper = select.closest('.variant-input-wrapper');
      const labelValueEl = wrapper?.querySelector('.form__label .font-medium');
      if (labelValueEl) labelValueEls.set(select, { el: labelValueEl, placeholderHTML: labelValueEl.innerHTML });
    });

    function hasMatchingVariant(optionName, value, precedingSelections) {
      return variants.some((variant) => {
        if (variant.options[optionName] !== value) return false;
        return Object.keys(precedingSelections).every(
          (otherName) => variant.options[otherName] === precedingSelections[otherName]
        );
      });
    }

    function getValidValues(select, precedingSelections) {
      const optionName = select.dataset.optionName;
      const originals = originalOptions.get(select);
      return originals
        .filter((opt) => opt.value && hasMatchingVariant(optionName, opt.value, precedingSelections))
        .map((opt) => opt.value);
    }

    function renderSelect(select, validValues) {
      const originals = originalOptions.get(select);
      const currentValue = select.value;
      const keepCurrent = validValues.includes(currentValue);

      select.textContent = '';
      originals.forEach((opt) => {
        if (!opt.value) {
          if (!keepCurrent) select.appendChild(opt.cloneNode(true));
          return;
        }
        if (validValues.includes(opt.value)) {
          select.appendChild(opt.cloneNode(true));
        }
      });

      if (keepCurrent) {
        select.value = currentValue;
        updateSelectedAttribute(select);
      }
    }

    function captureSelections() {
      const selections = {};
      selects.forEach((select) => {
        if (select.value) selections[select.dataset.optionName] = select.value;
      });
      lastSelections = selections;
    }

    // Every option's label value span starts out holding the "Select {name}"
    // placeholder (server-rendered, same markup for Model/Size/Base now that
    // none of them gets a default); once a select has a value, that area
    // shows nothing at all, per the product owners' instruction.
    function syncLabels() {
      selects.forEach((select) => {
        const info = labelValueEls.get(select);
        if (!info) return;
        info.el.innerHTML = select.value ? '' : info.placeholderHTML;
      });
    }

    function refresh() {
      for (let pass = 0; pass < selects.length + 1; pass++) {
        let autoSelected = false;

        selects.forEach((select, index) => {
          const precedingSelections = {};
          for (let j = 0; j < index; j++) {
            const prior = selects[j];
            if (prior.value) precedingSelections[prior.dataset.optionName] = prior.value;
          }

          const validValues = getValidValues(select, precedingSelections);
          renderSelect(select, validValues);

          const allowAutoSelect = select.dataset.optionName !== 'Size';
          if (allowAutoSelect && !select.value && validValues.length === 1) {
            select.value = validValues[0];
            updateSelectedAttribute(select);
            autoSelected = true;
          }
        });

        if (!autoSelected) break;
      }

      captureSelections();
      syncLabels();
    }

    // Re-applies the shopper's tracked picks (not necessarily a resolved
    // variant - see file-level comment) to this freshly swapped-in subtree,
    // skipping any pick that no longer appears among this select's raw
    // option values. The subsequent refresh() clears anything that turns
    // out invalid for the current Model/Size combination.
    function restoreSelections() {
      if (!lastSelections) return;
      selects.forEach((select) => {
        const desired = lastSelections[select.dataset.optionName];
        if (!desired) return;
        const originals = originalOptions.get(select);
        if (originals.some((opt) => opt.value === desired)) {
          select.value = desired;
          updateSelectedAttribute(select);
        }
      });
    }

    picker.addEventListener('change', refresh);
    restoreSelections();
    refresh();
    enforceCompleteSelection(picker, selects);

    return {};
  }

  // Finds this product's submit button(s) and the hidden variant-id input(s)
  // feeding them - the inline form (named by <product-info form="...">) and,
  // if present, the separate sticky form (ProductStickyForm-{section}-{product},
  // looked up the same way ProductInfo itself does it). Scoped via the
  // closest <product-info> so this never touches another product's form.
  function getProductForms(picker) {
    const productInfo = picker.closest('product-info');
    if (!productInfo) return [];

    const forms = [];
    const formId = productInfo.getAttribute('form');
    const mainForm = formId && document.getElementById(formId);
    if (mainForm) forms.push(mainForm);

    const sectionId = productInfo.getAttribute('data-section-id');
    const productId = productInfo.getAttribute('data-product-id');
    if (sectionId && productId) {
      const stickyForm = document.getElementById(`ProductStickyForm-${sectionId}-${productId}`);
      if (stickyForm) forms.push(stickyForm);
    }

    return forms;
  }

  // See file-level comment: Shopify's own resolution can silently complete a
  // variant from a partial pick, enabling the button and populating the
  // hidden id input with a Base (or other option) the shopper never chose.
  // This re-derives completeness strictly from the visible selects - every
  // one must have an explicit value - and overrides the button/hidden input
  // whenever that disagrees with what Shopify just did.
  function enforceCompleteSelection(picker, selects) {
    const allChosen = selects.every((select) => select.value !== '');
    if (allChosen) return;

    getProductForms(picker).forEach((form) => {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = '';

      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      button.setAttribute('disabled', '');
      button.setAttribute('unavailable', '');
      const textEl = button.querySelector('.btn-text > span') || button.querySelector('.btn-text');
      if (textEl) textEl.textContent = 'Select Options';
    });
  }

  function setUpAllPickers() {
    document.querySelectorAll('variant-picker').forEach((picker) => {
      setUpPicker(picker);
    });
  }

  setUpAllPickers();

  // Catches every ProductInfo DOM swap unconditionally (see file-level
  // comment) by watching for a freshly-inserted <variant-picker> node,
  // rather than depending on theme.pubsub.PUB_SUB_EVENTS.variantChange,
  // which ProductInfo only publishes when the swap happens to resolve a
  // complete variant.
  const observer = new MutationObserver((mutations) => {
    const sawNewPicker = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === 1 && (node.matches?.('variant-picker') || node.querySelector?.('variant-picker'))
      )
    );
    if (sawNewPicker) setUpAllPickers();
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
